import { msg } from '@gonfalon/messages/msg';

import { type UsageDetailSearchParams, useFilteredDimensions } from '../../../../hooks';
import type { AggregationType, DimensionType, MonthOption, UsageTabKey } from '../../../../types';
import { Granularity, TimeRangeOption } from '../../../../types';
import { usageTabConfig } from '../../../constants/usageTabConfig';

import { AggregationButtonGroup } from './AggregationButtonGroup';
import { DimensionPicker } from './DimensionPicker';
import { MonthPicker } from './MonthPicker';
import { TimeRangeButtonGroup } from './TimeRangeButtonGroup';

import styles from './UsageDetailControls.module.css';

type UsageDetailControlsProps = {
  setParams: (updater: (prev: UsageDetailSearchParams) => UsageDetailSearchParams) => void;
  usageTab: UsageTabKey;
  selectedTimeRange?: TimeRangeOption;
  selectedMonth?: string;
  monthOptions: MonthOption[];
  selectedAggregationType: AggregationType;
  selectedDimension?: DimensionType;
  granularity: Granularity;
  availableDimensions?: DimensionType[];
  children?: React.ReactNode;
};

export function UsageDetailControls({
  setParams,
  usageTab,
  selectedTimeRange,
  selectedMonth,
  monthOptions,
  selectedAggregationType,
  selectedDimension,
  granularity,
  availableDimensions,
  children,
}: UsageDetailControlsProps) {
  const { meterType, granularities } = usageTabConfig[usageTab];
  const tabDimensions = useFilteredDimensions(usageTab);
  const dimensions = availableDimensions ?? tabDimensions;
  const showTimeRangeControls = !!selectedTimeRange;
  const showMonthPicker = !selectedTimeRange || selectedTimeRange === TimeRangeOption.MONTH_TO_DATE;

  const renderFirstRowMiddleControls = () => {
    if (!showTimeRangeControls) {
      return renderSecondRowControls();
    }

    return (
      <TimeRangeButtonGroup
        aria-label={msg('billing.usageControls.timeRangeLabel')}
        selectedKey={selectedTimeRange}
        handlePress={(nextTimeRange: TimeRangeOption) => setParams((prev) => ({ ...prev, timeRange: nextTimeRange }))}
        granularities={granularities}
      />
    );
  };

  const renderSecondRowControls = () => {
    if (granularity === Granularity.MONTHLY) {
      return null;
    }

    return (
      <AggregationButtonGroup
        aria-label={msg('billing.usageControls.aggregationLabel')}
        tab={usageTab}
        meterType={meterType}
        selectedKey={selectedAggregationType}
        handlePress={(nextAggregationType: AggregationType) =>
          setParams((prev) => ({ ...prev, aggregationType: nextAggregationType }))
        }
      />
    );
  };

  return (
    <>
      <div className={styles.topLevelControls}>
        {dimensions.length > 0 && (
          <DimensionPicker
            aria-label={msg('billing.usageControls.dimensionLabel')}
            value={selectedDimension}
            onChange={(nextDimension: DimensionType | undefined) =>
              setParams((prev) => ({ ...prev, dimension: nextDimension }))
            }
            dimensions={dimensions}
          />
        )}
        {renderFirstRowMiddleControls()}
        {showMonthPicker && (
          <MonthPicker
            aria-label={msg('billing.usageControls.monthLabel')}
            value={selectedMonth}
            onChange={(nextMonth: string) => setParams((prev) => ({ ...prev, month: nextMonth }))}
            monthOptions={monthOptions}
          />
        )}
      </div>
      {children}
      {showTimeRangeControls && renderSecondRowControls()}
    </>
  );
}
